import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {TargetProfileDetailField} from './TargetProfileDetailField';
import {TargetProfileSkillSection} from './TargetProfileSkillSection';
import {Loading} from '../../../../../components/Loading/Loading';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import type {StandardSkill} from '../../../profile/types';
import {
  SkillCategory,
  TargetProfileDetail,
  TargetProfileSkill,
} from '../../types/DepartmentTargetProfileTypes';

export interface Props {
  profile: TargetProfileDetail | null | undefined;
  isLoading: boolean;
  excludedCoreSkillIds?: ReadonlySet<number>;
  excludedHardSkillIds?: ReadonlySet<number>;
  excludedToolSkillIds?: ReadonlySet<number>;
  excludedDomainKnowledgeSkillIds?: ReadonlySet<number>;
  excludedSoftSkillIds?: ReadonlySet<number>;
  excludedResponsibilityIds?: ReadonlySet<number>;
  onToggleCoreExclusion?: (standardSkillId: number) => void;
  onToggleHardExclusion?: (standardSkillId: number) => void;
  onToggleToolExclusion?: (standardSkillId: number) => void;
  onToggleDomainKnowledgeExclusion?: (standardSkillId: number) => void;
  onToggleSoftExclusion?: (standardSkillId: number) => void;
  onToggleResponsibilityExclusion?: (standardSkillId: number) => void;
  onRemoveSkill?: (standardSkillId: number) => void;
  onToggleRequirementLevel?: (skill: TargetProfileSkill) => void;
  onAddSkill?: (
    skillCategory: SkillCategory,
    skill: StandardSkill,
    requirementLevel: 'must_have' | 'nice_to_have'
  ) => void;
  onAddCustomSkill?: (
    skillCategory: SkillCategory,
    name: string,
    requirementLevel: 'must_have' | 'nice_to_have'
  ) => void;
  skillCurationDisabled?: boolean;
  variant?: 'card' | 'flat';
}

export const TargetProfileDetailCard = ({
  profile,
  isLoading,
  excludedCoreSkillIds,
  excludedHardSkillIds,
  excludedToolSkillIds,
  excludedDomainKnowledgeSkillIds,
  excludedSoftSkillIds,
  excludedResponsibilityIds,
  onToggleCoreExclusion,
  onToggleHardExclusion,
  onToggleToolExclusion,
  onToggleDomainKnowledgeExclusion,
  onToggleSoftExclusion,
  onToggleResponsibilityExclusion,
  onRemoveSkill,
  onToggleRequirementLevel,
  onAddSkill,
  onAddCustomSkill,
  skillCurationDisabled,
  variant = 'card',
}: Props) => {
  const isExclusionEditable = Boolean(
    onToggleCoreExclusion &&
      onToggleHardExclusion &&
      onToggleToolExclusion &&
      onToggleDomainKnowledgeExclusion &&
      onToggleSoftExclusion &&
      onToggleResponsibilityExclusion
  );
  const isCurationEditable = Boolean(onRemoveSkill && onAddSkill && onAddCustomSkill);
  const containerSx =
    variant === 'flat'
      ? {mb: 0, p: 0, border: 'none', bgcolor: 'transparent', borderRadius: 0}
      : {
        mb: 3,
        p: 2,
        borderRadius: '16px',
        border: `1px solid ${orangeColors[3]}`,
        bgcolor: orangeColors[5],
      };
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal.targetProfiles.detail'});
  const {t: tShared} = useTranslation();

  const metaItems = useMemo(() => {
    if (!profile) {
      return [];
    }

    return [
      profile.seniority
        ? {label: t('seniority'), value: getSeniorityLevelLabel(profile.seniority, tShared)}
        : null,
      profile.experienceLevel ? {label: t('experienceLevel'), value: profile.experienceLevel} : null,
      profile.industry ? {label: t('industry'), value: profile.industry} : null,
      profile.geography ? {label: t('geography'), value: profile.geography} : null,
    ].filter((item): item is {label: string; value: string} => item !== null);
  }, [profile, t, tShared]);

  const aliases = profile?.occupationAliases[currentLanguage] ?? [];
  const excludeSkillLabel = String(t('excludeSkill'));
  const includeSkillLabel = String(t('includeSkill'));
  const removeSkillLabel = String(t('removeSkill'));
  const toggleRequirementLabel = String(t('toggleRequirement'));
  const addSkillLabel = String(t('addSkill'));
  const createSkillLabel = (name: string) => String(t('createSkill', {name}));

  const occupationDescription =
    profile?.occupation && currentLanguage === 'de'
      ? profile.occupation.descriptionDe
      : profile?.occupation?.descriptionEn;

  if (isLoading) {
    return (
      <Box sx={{...containerSx, ...(variant === 'card' && {bgcolor: 'background.paper'})}}>
        <Loading />
      </Box>
    );
  }

  if (!profile) {
    return null;
  }

  const sections: Array<{
    label: string;
    skillCategory: SkillCategory;
    skills: TargetProfileSkill[];
    excluded?: ReadonlySet<number>;
    onToggle?: (id: number) => void;
  }> = [
    {
      label: t('coreCompetencies'),
      skillCategory: 'core_competency',
      skills: profile.coreCompetencies ?? [],
      excluded: excludedCoreSkillIds,
      onToggle: onToggleCoreExclusion,
    },
    {
      label: t('hardSkills'),
      skillCategory: 'hard_skill',
      skills: profile.hardSkills ?? [],
      excluded: excludedHardSkillIds,
      onToggle: onToggleHardExclusion,
    },
    {
      label: t('tools'),
      skillCategory: 'tool',
      skills: profile.tools ?? [],
      excluded: excludedToolSkillIds,
      onToggle: onToggleToolExclusion,
    },
    {
      label: t('domainKnowledge'),
      skillCategory: 'domain_knowledge',
      skills: profile.domainKnowledge ?? [],
      excluded: excludedDomainKnowledgeSkillIds,
      onToggle: onToggleDomainKnowledgeExclusion,
    },
    {
      label: t('softSkills'),
      skillCategory: 'soft_skill',
      skills: profile.softSkills ?? [],
      excluded: excludedSoftSkillIds,
      onToggle: onToggleSoftExclusion,
    },
    {
      label: t('responsibilities'),
      skillCategory: 'responsibility',
      skills: profile.responsibilities ?? [],
      excluded: excludedResponsibilityIds,
      onToggle: onToggleResponsibilityExclusion,
    },
  ];

  return (
    <Box sx={containerSx}>
      <Typography variant='subtitle1' sx={{fontWeight: 700, color: greenColors[900], mb: 0.5}}>
        {profile.title}
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{mb: occupationDescription?.trim() ? 0.5 : 1.5}}
      >
        {profile.occupation.title} · {profile.professionalArea.name[currentLanguage]}
      </Typography>
      {occupationDescription?.trim() && (
        <Typography variant='body2' color='text.secondary' sx={{mb: 1.5}}>
          {occupationDescription}
        </Typography>
      )}

      {metaItems.length > 0 && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5}}>
          {metaItems.map((item) => (
            <Typography key={item.label} variant='body2'>
              <Box component='span' sx={{fontWeight: 600, color: greenColors[700]}}>
                {item.label}:
              </Box>{' '}
              {item.value}
            </Typography>
          ))}
        </Box>
      )}

      <TargetProfileDetailField label={t('educationRequirements')} value={profile.educationRequirements} />
      <TargetProfileDetailField label={t('workingStyleNotes')} value={profile.workingStyleNotes} />

      {sections.map((section) => (
        <TargetProfileSkillSection
          key={section.label}
          label={section.label}
          skills={section.skills}
          language={currentLanguage}
          mustHaveLabel={t('mustHave')}
          niceToHaveLabel={t('niceToHave')}
          disabled={skillCurationDisabled}
          {...(isExclusionEditable && {
            excludedSkillIds: section.excluded,
            onToggleSkillExclusion: section.onToggle,
            excludeSkillLabel,
            includeSkillLabel,
          })}
          {...(isCurationEditable && {
            onRemoveSkill,
            onToggleRequirementLevel,
            onAddSkill: (skill: StandardSkill, requirementLevel: 'must_have' | 'nice_to_have') =>
              onAddSkill?.(section.skillCategory, skill, requirementLevel),
            onAddCustomSkill: (name: string, requirementLevel: 'must_have' | 'nice_to_have') =>
              onAddCustomSkill?.(section.skillCategory, name, requirementLevel),
            skillCategory: section.skillCategory,
            occupationResourceId: profile.occupation.resourceId,
            removeSkillLabel,
            toggleRequirementLabel,
            addSkillLabel,
            createSkillLabel,
          })}
        />
      ))}

      {aliases.length > 0 && (
        <Box>
          <Typography variant='caption' sx={{color: greenColors[700], fontWeight: 600, display: 'block', mb: 0.5}}>
            {t('aliases')}
          </Typography>
          <Typography variant='body2'>{aliases.map((entry) => entry.alias).join(', ')}</Typography>
        </Box>
      )}
    </Box>
  );
};
